import React, { useState } from "react";
import { Button, Card, CardBody, Col, Container, Form, FormFeedback, Input, Label, Row, Spinner } from "reactstrap";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import ParticlesAuth from "./ParticlesAuth";
import withRouter from "../../Components/withRouter";
import logoLight from "../../assets/images/logo-light.png";
import { logoutUser } from "../../slices/thunks";
import { postFirstLoginUpdateUserPassowrd } from "../../apiCalls/api_calls";
import { useTranslation } from 'react-i18next';

const FirstLogin = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const passRegExp = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: "",
      confirm_password: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .matches(passRegExp, t('auth.validation.passwordRequired2'))
        .required(t('auth.validation.passwordMissing')),
      confirm_password: Yup.string()
        .oneOf([Yup.ref("password")], t('auth.validation.passwordConfirmMismatch'))
        .required(t('auth.validation.passwordConfirmRequired2')),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        await postFirstLoginUpdateUserPassowrd({ password: values.password });
        toast(t('auth.passwordChangedToast'), {
          position: "bottom-right",
          className: "bg-success text-white",
        });
        dispatch(logoutUser(props.router.navigate));
      } catch (err) {
        toast(typeof err === "string" ? err : err?.message || t('auth.networkError'), {
          position: "bottom-right",
          className: "bg-danger text-white",
          autoClose: 1500,
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const handleLogout = () => {
    dispatch(logoutUser(props.router.navigate));
  };

  document.title = `${t('auth.firstLoginTitle')} | Shadow System B2B`;

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <Link to="/" className="d-inline-block auth-logo">
                      <img src={logoLight} alt="" height="45" />
                    </Link>
                  </div>
                  <p className="mt-3 fs-15 fw-medium">{t('auth.panelB2b')}</p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h4 className="text-primary">{t('auth.firstLoginTitle')}</h4>
                      <p className="text-muted">
                        {t('auth.firstLoginSubtitle')}
                      </p>
                    </div>

                    <div className="p-2 mt-4">
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                      >
                        <div className="mb-3">
                          <Label className="form-label" htmlFor="password-input">
                            {t('auth.password')} <span className="text-danger">*</span>
                          </Label>
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              id="password-input"
                              name="password"
                              value={validation.values.password || ""}
                              type={showPassword ? "text" : "password"}
                              className="form-control pe-5"
                              placeholder={t('auth.passwordPlaceholder')}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={validation.touched.password && !!validation.errors.password}
                            />
                            {validation.touched.password && validation.errors.password ? (
                              <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                            ) : null}
                            {validation.values.password ? (
                              <button
                                className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted shadow-none"
                                onClick={() => setShowPassword(!showPassword)}
                                type="button"
                                id="password-addon"
                              >
                                <i className="ri-eye-fill align-middle"></i>
                              </button>
                            ) : null}
                          </div>
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="confirmPassword" className="form-label">
                            {t('auth.confirmPassword')} <span className="text-danger">*</span>
                          </Label>
                          <Input
                            id="confirmPassword"
                            name="confirm_password"
                            type="password"
                            placeholder={t('auth.confirmPasswordPlaceholder')}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.confirm_password || ""}
                            invalid={validation.touched.confirm_password && !!validation.errors.confirm_password}
                          />
                          {validation.touched.confirm_password && validation.errors.confirm_password ? (
                            <FormFeedback type="invalid">{validation.errors.confirm_password}</FormFeedback>
                          ) : null}
                        </div>

                        <div className="mt-4">
                          <Button color="primary" disabled={loading} className="w-100" type="submit">
                            {loading ? <Spinner size="sm" className="me-2" /> : null}
                            {t('auth.saveNewPassword')}
                          </Button>
                        </div>
                      </Form>
                    </div>

                    <div className="text-center mt-2">
                      <Link onClick={handleLogout} className="d-inline-block">
                        {t('auth.logout')}
                      </Link>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default withRouter(FirstLogin);
